import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type TransitionEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@components/atoms/Icon'
import { PageTitleBar } from '@components/shell/PageTitleBar'
import styles from './SlidePanel.module.scss'

export interface SlidePanelProps {
  open: boolean
  onClose: () => void
  panelContent: ReactNode
  title: string
  /** Optional sticky footer rendered below the scrollable content area. */
  footer?: ReactNode
}

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

/**
 * Full-screen overlay that slides in from the right edge of the viewport,
 * pinned PageTitleBar at the top with a close (X) action, and an
 * arbitrarily-tall scrollable content area below.
 *
 * Lifecycle:
 *   - When `open` flips true: portal mounts at `document.body`, the panel
 *     starts off-screen (`translateX(100%)`) and the next animation frame
 *     transitions it into place.
 *   - When `open` flips false: the panel transitions out, then unmounts
 *     after the `transform` `transitionend` fires (so the close animation
 *     actually plays before the DOM is removed).
 *
 * Side-effects while open:
 *   - Body scroll is locked.
 *   - ESC closes the panel.
 *   - Focus is moved to the close button on open and trapped inside the
 *     panel via Tab / Shift+Tab cycling.
 *   - Previously-focused element is restored on close.
 */
export function SlidePanel({
  open,
  onClose,
  panelContent,
  title,
  footer,
}: SlidePanelProps) {
  // `shouldRender` keeps the portal in the tree while the close animation
  // plays. `isVisible` flips the CSS class that drives the slide.
  const [shouldRender, setShouldRender] = useState(open)
  const [isVisible, setIsVisible] = useState(false)

  const panelRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)

  // ---- Mount/unmount + slide direction --------------------------------------
  // Two-phase open: first mount the panel off-screen (`translateX(100%)`),
  // then — in a layout effect, after a forced reflow — flip to `panelOpen`
  // so the transform transitions in. rAF-based deferral proved unreliable
  // here: the `.frameClip` + `will-change` compositor layer let the browser
  // coalesce both states into one recalc, so the panel snapped in with no
  // animation. Reading layout (`getBoundingClientRect`) synchronously
  // commits the off-screen start state, guaranteeing the slide-in animates.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (open) {
      // Capture the element to restore focus to on close.
      previouslyFocusedRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      setShouldRender(true)
      return undefined
    }
    setIsVisible(false)
    return undefined
  }, [open])

  useLayoutEffect(() => {
    if (!open || !shouldRender) return
    const panel = panelRef.current
    if (!panel) return
    // Force the browser to lay out the off-screen `translateX(100%)` start
    // state before we add `panelOpen`, so the change to `translateX(0)`
    // registers as a transition rather than an instant jump.
    void panel.getBoundingClientRect()
    setIsVisible(true)
  }, [open, shouldRender])
  /* eslint-enable react-hooks/set-state-in-effect */

  function handleTransitionEnd(event: TransitionEvent<HTMLDivElement>) {
    // Only the panel's own transform transition matters — ignore bubbling
    // from nested elements (Tabs, ThemeSwitcher, etc.).
    if (event.target !== event.currentTarget) return
    if (event.propertyName !== 'transform') return
    if (!isVisible && !open) {
      setShouldRender(false)
      // Restore focus to the element that opened us.
      previouslyFocusedRef.current?.focus()
    }
  }

  // ---- ESC to close ---------------------------------------------------------
  useEffect(() => {
    if (!shouldRender) return undefined
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [shouldRender, onClose])

  // ---- Body scroll lock -----------------------------------------------------
  useEffect(() => {
    if (!shouldRender) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [shouldRender])

  // ---- Focus management -----------------------------------------------------
  useEffect(() => {
    if (!isVisible) return undefined
    // Move focus to the close button on open so keyboard users can dismiss
    // the panel immediately and screen readers announce the dialog name.
    closeButtonRef.current?.focus()

    function handleTab(event: KeyboardEvent) {
      if (event.key !== 'Tab' || !panelRef.current) return
      const focusables = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(
        // Skip focusables hidden via `display: none` / collapsed flex grids.
        (el) =>
          el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement,
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleTab)
    return () => document.removeEventListener('keydown', handleTab)
  }, [isVisible])

  if (!shouldRender) return null

  return createPortal(
    <div className={styles.frameClip}>
      <div
        ref={panelRef}
        className={[styles.panel, isVisible ? styles.panelOpen : '']
          .filter(Boolean)
          .join(' ')}
        onTransitionEnd={handleTransitionEnd}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className={styles.header}>
          <PageTitleBar
            title={title}
            variant="lg"
            rightSlot={
              <button
                ref={closeButtonRef}
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close panel"
              >
                <Icon name="x" size={24} color="currentColor" />
              </button>
            }
          />
        </header>

        <div className={styles.content}>{panelContent}</div>

        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  )
}
